import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/infrastructure/api/client'
import type {
  LocaleTranslationState,
  TranslationDashboardRow,
  TaxonomyTerm,
  InvestigationTranslationBreakdown,
  LeadTranslationBreakdown,
} from '@/domains/types'

export const translationKeys = {
  entityState: (entityType: string, entityId: string) =>
    ['translations', entityType, entityId] as const,
  dashboard: ['translations', 'dashboard'] as const,
  investigationBreakdown: (investigationId: string) =>
    ['translations', 'dashboard', 'investigation', investigationId] as const,
  leadBreakdown: (leadId: string) => ['translations', 'dashboard', 'lead', leadId] as const,
  investigationCategoryTerms: (investigationId: string) =>
    ['translations', 'investigation', investigationId, 'category-terms'] as const,
}

/**
 * Per-locale raw translation state for one entity — feeds the language tabs'
 * badges and field editors on the edit forms (TRANSLATION-PLAN.md §5). Not
 * locale-scoped in the query key the way content reads are: this always
 * returns every non-source locale's state at once, unlike `?lang=` reads.
 */
export function useGetEntityTranslationState(
  entityType: 'investigation' | 'lead' | 'fact',
  entityId: string,
) {
  return useQuery({
    queryKey: translationKeys.entityState(entityType, entityId),
    queryFn: async (): Promise<LocaleTranslationState[]> => {
      const response = await apiClient.get<LocaleTranslationState[]>(
        `/translations/${entityType}/${entityId}`,
      )
      return response.data
    },
    enabled: !!entityId,
  })
}

/**
 * The category terms linked to an investigation, in display order, each with
 * ALL its labels — for the edit form. Locale-free on purpose: unlike
 * `?lang=` reads it never overlays translated text on the source fields, so
 * the form can hydrate the source language safely. The key sits under
 * `['translations', 'investigation', id]`, which saving an investigation
 * already invalidates.
 */
export function useGetInvestigationCategoryTerms(investigationId: string) {
  return useQuery({
    queryKey: translationKeys.investigationCategoryTerms(investigationId),
    queryFn: async (): Promise<TaxonomyTerm[]> => {
      const response = await apiClient.get<TaxonomyTerm[]>(
        `/investigations/${investigationId}/category-terms`,
      )
      return response.data
    },
    enabled: !!investigationId,
  })
}

/** The translation dashboard (TRANSLATION-PLAN.md §9 Phase 4) — management-only. */
export function useGetTranslationDashboard() {
  return useQuery({
    queryKey: translationKeys.dashboard,
    queryFn: async (): Promise<TranslationDashboardRow[]> => {
      const response = await apiClient.get<TranslationDashboardRow[]>('/translations/dashboard')
      return response.data
    },
  })
}

/** The investigation drill-down page: its own completeness plus one node per
 * lead, each already cascaded with its own facts. A missing investigation is
 * an expected outcome here (a stale link, a deleted investigation), not an
 * unexpected error — `_skipErrorToast` leaves the page's own "not found"
 * message as the only feedback, instead of also popping a generic toast. */
export function useGetInvestigationTranslationBreakdown(investigationId: string) {
  return useQuery({
    queryKey: translationKeys.investigationBreakdown(investigationId),
    queryFn: async (): Promise<InvestigationTranslationBreakdown> => {
      const response = await apiClient.get<InvestigationTranslationBreakdown>(
        `/translations/dashboard/investigation/${investigationId}/leads`,
        { _skipErrorToast: true },
      )
      return response.data
    },
    enabled: !!investigationId,
    retry: false,
  })
}

/** The lead drill-down page: its own completeness plus one node per fact. See
 * `useGetInvestigationTranslationBreakdown` for why 404s skip the toast. */
export function useGetLeadTranslationBreakdown(leadId: string) {
  return useQuery({
    queryKey: translationKeys.leadBreakdown(leadId),
    queryFn: async (): Promise<LeadTranslationBreakdown> => {
      const response = await apiClient.get<LeadTranslationBreakdown>(
        `/translations/dashboard/lead/${leadId}/facts`,
        { _skipErrorToast: true },
      )
      return response.data
    },
    enabled: !!leadId,
    retry: false,
  })
}
